import { useMutation } from '@tanstack/react-query';
import { get } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { IProduct, IVariationAsset } from 'src/interfaces';
import { queryKeys, requestCCApi } from 'src/utils';

import { uploadCCAsset } from '../assets';
import { narrativeGenerators } from '../userActions/helper';
import { updateProductVariationsWithAssets } from './helper';

export const useCreateProduct = () => {
  return useMutation({
    mutationFn: createProduct,
    onSettled: () => {
      queryClient.invalidateQueries(queryKeys.assets.invalidateAll());
      queryClient.invalidateQueries(
        queryKeys.productVariations.invalidateAll(),
      );
      queryClient.invalidateQueries(queryKeys.products.invalidateAll());
      queryClient.invalidateQueries(queryKeys.ccAssets.invalidateAll());
      queryClient.invalidateQueries(
        queryKeys.productPublications.invalidateAll(),
      );
    },
  });
};

const createProduct = async (
  product: IProduct & { asset?: File } & {
    allVariationAssets: IVariationAsset[];
  },
) => {
  let assetId: number[] = product.asset_id || [];

  const existingAsset = product.allVariationAssets.find(
    (asset) => asset.name === product.asset?.name,
  );

  if (product.asset) {
    if (existingAsset) {
      assetId = [existingAsset.id];
    } else {
      assetId = await uploadCCAsset([product.asset.name]);
    }
  }

  const { finalProductVariations, updateAssetThemeTransaction } =
    await updateProductVariationsWithAssets(product);
  const productAssetId = assetId?.[0];

  const data = {
    data: [
      {
        resource: COLLECTIONS.PRODUCTS,
        method: 'PUT',
        id: get(product, 'product_code', ''),
        data: {
          asset_id: assetId,
          name_override: get(product, 'name_override', null),
          active: get(product, 'active', false),
          product_category_id: get(product, 'product_category_id', []),
          ...(assetId.length > 0 && {
            asset_narrative: narrativeGenerators[COLLECTIONS.ASSETS][
              USER_ACTIONS.UPDATE
            ](existingAsset ? existingAsset.name : product.asset?.name),
          }),
          narrative: (
            narrativeGenerators[COLLECTIONS.PRODUCTS][USER_ACTIONS.CREATE] as (
              productName: string,
            ) => string
          )(
            (product?.name_override ??
              (product?.productVariations.length > 0
                ? product?.productVariations[0].name
                : '')) as string,
          ),
        },
      },
      ...finalProductVariations.map((item) => ({
        resource: COLLECTIONS.PRODUCT_VARIATIONS,
        method: 'PUT',
        id: get(item, 'product_code', ''),
        data: {
          group_product_code: get(product, 'product_code', ''),
          product_variation_size_id: get(item, 'product_variation_size_id'),
          product_variation_flavor_id: get(item, 'product_variation_flavor_id'),
          name: get(item, 'name', ''),
          description: get(item, 'description', ''),
          disclaimer: get(item, 'disclaimer', ''),
          volume: get(item, 'volume', 0),
          nutrition: get(item, 'nutrition', '000'),
          active: get(item, 'active', false),
          asset_id: item?.asset_id?.filter((id) => id !== productAssetId) || [],
          dy_asset: item?.dy_asset === productAssetId ? null : item?.dy_asset,
          narrative: (
            narrativeGenerators[COLLECTIONS.PRODUCT_VARIATIONS][
              USER_ACTIONS.CREATE
            ] as (
              productFlavorName: string,
              productVariantName: string,
              productName: string,
            ) => string
          )(
            item?.productFlavorName || '',
            item?.productVariantName || '',
            item?.name || '',
          ),
        },
      })),
      ...updateAssetThemeTransaction,
    ],
  };

  const response = await requestCCApi({
    url: '/transaction',
    method: 'POST',
    data: data,
  });

  return response;
};
